import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LUNAR } from '../constants/testIds';
import { LayoutDashboard, Award, Hammer, BookOpen, Clock, AlertCircle, Ship, Bookmark, ArrowUpRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPanel } from '../components/AdminPanel';

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Determine admin synchronously from the cached session so the panel opens on first paint.
  const cachedIsAdmin = (() => {
    try {
      const cached = JSON.parse(localStorage.getItem('lunar_user') || 'null');
      return cached?.role === 'admin';
    } catch {
      return false;
    }
  })();
  const isAdmin = user?.role === 'admin' || cachedIsAdmin;

  // Selected tab: 'bids', 'consignments', 'saved', 'admin'
  const [activeTab, setActiveTab] = useState(cachedIsAdmin ? 'admin' : 'bids');

  // Once the live user resolves, keep admins on the management panel (runs once).
  const didInitTab = useRef(cachedIsAdmin);
  useEffect(() => {
    if (!didInitTab.current && user?.role === 'admin') {
      setActiveTab('admin');
      didInitTab.current = true;
    }
  }, [user]);
  
  // Dashboard summary data
  const [dashboardData, setDashboardData] = useState(null);
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.info('Authorization Required', {
        description: 'Please authenticate to access your luxury collector dashboard.'
      });
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  // Fetch Dashboard Summary & Saved Items
  useEffect(() => {
    const fetchDashboardDetails = async () => {
      if (!user) return;
      setLoading(true);
      try {
        // Fetch summary
        const summaryResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/summary`);
        setDashboardData(summaryResponse.data);

        // Fetch saved artifacts details
        const savedResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/artifacts/user/saved`);
        setSavedItems(savedResponse.data);
      } catch (err) {
        console.error('Error loading dashboard summary', err);
        toast.error('Load Error', { description: 'Unable to synchronize collector ledger.' });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardDetails();
  }, [user]);

  // Handle remove from saved items
  const removeSavedItem = async (id, title) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/artifacts/${id}/save`);
      setSavedItems(savedItems.filter(item => item.id !== id));
      if (dashboardData) {
        setDashboardData({
          ...dashboardData,
          saved_count: Math.max(0, dashboardData.saved_count - 1)
        });
      }
      toast.info('Archive Removed', {
        description: `"${title}" removed from your archives.`
      });
    } catch (err) {
      toast.error('Error', { description: 'Unable to remove archive.' });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-luxury-black text-white flex items-center justify-center font-serif-luxury text-luxury-gold tracking-[0.3em] animate-pulse">
        CALIBRATING COLLECTOR LEDGER...
      </div>
    );
  }

  const { bids = [], consignments = [] } = dashboardData || {};

  return (
    <div className="min-h-screen bg-luxury-black text-white py-12 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-white/10 pb-8 mb-12">
          <div data-testid={LUNAR.dashboardWelcome}>
            <span className="text-[10px] uppercase tracking-[0.3em] text-luxury-gold font-bold flex items-center gap-1.5 mb-2">
              <LayoutDashboard size={14} className="text-luxury-gold" /> SECURE CONTROL ROOM
            </span>
            <h1 className="text-3xl md:text-5xl font-serif-luxury text-white">
              Welcome, <span className="italic text-luxury-goldLight">{user?.name}</span>
            </h1>
            <p className="text-xs text-luxury-muted mt-2 font-sans-luxury">
              Account Reference: {user?.email} • Cleared Tier: {user?.role === 'admin' ? 'Administrator Curator' : 'Collector Class A'}
            </p>
          </div>

          <Link 
            to="/consign" 
            className="btn-gold mt-6 md:mt-0 font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-6 py-3.5 rounded-none self-start md:self-center"
          >
            Consign New Artifact
          </Link>
        </div>

        {/* Admin: management panel shown directly — nothing hidden behind tabs */}
        {isAdmin && <AdminPanel />}

        {/* Collector-only dashboard (bids / consignments / archives) */}
        {!isAdmin && (
        <>
        {/* Bento Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          {/* Active Bids Bento */}
          <div className="bg-luxury-charcoal border border-white/5 p-6 relative overflow-hidden group hover:border-luxury-gold/30 transition-all duration-300">
            <span className="text-[9px] font-sans-luxury uppercase tracking-widest text-luxury-muted block mb-1">Your Registered Bids</span>
            <span className="text-3xl font-mono-luxury font-bold text-white block">{bids.length}</span>
            <span className="text-[10px] text-luxury-muted leading-none block mt-2">Active lots under competitive contract</span>
            <Hammer size={40} className="absolute right-[-10px] bottom-[-10px] text-white/5 stroke-[1] group-hover:text-luxury-gold/5 transition-all duration-500" />
          </div>

          {/* Consignments Bento */}
          <div className="bg-luxury-charcoal border border-white/5 p-6 relative overflow-hidden group hover:border-luxury-gold/30 transition-all duration-300">
            <span className="text-[9px] font-sans-luxury uppercase tracking-widest text-luxury-muted block mb-1">Specimen Consignments</span>
            <span className="text-3xl font-mono-luxury font-bold text-luxury-gold block">{consignments.length}</span>
            <span className="text-[10px] text-luxury-muted leading-none block mt-2">Historic items submitted to secure ledger</span>
            <Ship size={40} className="absolute right-[-10px] bottom-[-10px] text-white/5 stroke-[1] group-hover:text-luxury-gold/5 transition-all duration-500" />
          </div>

          {/* Saved Items Bento */}
          <div className="bg-luxury-charcoal border border-white/5 p-6 relative overflow-hidden group hover:border-luxury-gold/30 transition-all duration-300">
            <span className="text-[9px] font-sans-luxury uppercase tracking-widest text-luxury-muted block mb-1">Saved Items</span>
            <span className="text-3xl font-mono-luxury font-bold text-white block">{savedItems.length}</span>
            <span className="text-[10px] text-luxury-muted leading-none block mt-2">Saved and bookmarked artifacts</span>
            <Bookmark size={40} className="absolute right-[-10px] bottom-[-10px] text-white/5 stroke-[1] group-hover:text-luxury-gold/5 transition-all duration-500" />
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex flex-wrap border-b border-white/10 mb-8 font-sans-luxury text-xs tracking-widest uppercase">
          <button 
            onClick={() => setActiveTab('bids')}
            className={`py-4 px-6 border-b-2 transition-all duration-300 whitespace-nowrap flex-shrink-0 ${activeTab === 'bids' ? 'border-luxury-gold text-luxury-gold font-bold' : 'border-transparent text-luxury-muted hover:text-white'}`}
            data-testid={LUNAR.dashboardTabBids}
          >
            Active Bids ({bids.length})
          </button>
          <button 
            onClick={() => setActiveTab('consignments')}
            className={`py-4 px-6 border-b-2 transition-all duration-300 whitespace-nowrap flex-shrink-0 ${activeTab === 'consignments' ? 'border-luxury-gold text-luxury-gold font-bold' : 'border-transparent text-luxury-muted hover:text-white'}`}
            data-testid={LUNAR.dashboardTabConsignments}
          >
            My Consignments ({consignments.length})
          </button>
          <button 
            onClick={() => setActiveTab('saved')}
            className={`py-4 px-6 border-b-2 transition-all duration-300 whitespace-nowrap flex-shrink-0 ${activeTab === 'saved' ? 'border-luxury-gold text-luxury-gold font-bold' : 'border-transparent text-luxury-muted hover:text-white'}`}
            data-testid={LUNAR.dashboardTabSaved}
          >
            Archives ({savedItems.length})
          </button>
          {user?.role === 'admin' && (
            <button 
              onClick={() => setActiveTab('admin')}
              className={`py-4 px-6 border-b-2 transition-all duration-300 whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${activeTab === 'admin' ? 'border-luxury-gold text-luxury-gold font-bold' : 'border-transparent text-luxury-muted hover:text-white'}`}
              data-testid="dashboard-tab-admin"
            >
              <Shield size={13} /> Admin
            </button>
          )}
        </div>

        {/* TAB CONTENTS */}
        <div className="min-h-[300px]">
          
          {/* Active Bids Table */}
          {activeTab === 'bids' && (
            <div className="border border-white/5 bg-luxury-charcoal p-8" data-testid={LUNAR.dashboardBidsTable}>
              {bids.length === 0 ? (
                <div className="text-center py-12 text-luxury-muted font-sans-luxury space-y-4">
                  <AlertCircle size={28} className="mx-auto stroke-[1.2]" />
                  <p className="text-xs">No bidding contracts currently outstanding.</p>
                  <Link to="/" className="text-luxury-gold text-[10px] uppercase tracking-widest border-b border-luxury-gold pb-0.5">Explore Auction Gallery</Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs font-sans-luxury">
                    <thead>
                      <tr className="border-b border-white/10 text-luxury-gold uppercase text-[9px] tracking-widest">
                        <th className="pb-4 font-bold">Artifact Lot</th>
                        <th className="pb-4 font-bold">Your Max Hammer</th>
                        <th className="pb-4 font-bold">Registration Timestamp</th>
                        <th className="pb-4 font-bold text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {bids.map((bid) => (
                        <tr key={bid.id} className="hover:bg-white/2 transition-colors duration-200">
                          <td className="py-5 flex items-center space-x-4">
                            {bid.artifact_image && (
                              <img src={bid.artifact_image} alt={bid.artifact_title} className="w-10 h-10 object-cover border border-white/10" />
                            )}
                            <span className="font-serif-luxury text-sm text-white font-semibold">{bid.artifact_title}</span>
                          </td>
                          <td className="py-5 font-mono-luxury font-bold text-luxury-gold">
                            ${bid.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-5 text-luxury-muted font-mono-luxury text-[10px]">
                            {new Date(bid.timestamp).toLocaleString()}
                          </td>
                          <td className="py-5 text-right">
                            <span className="bg-green-500/10 border border-green-500/20 text-green-400 text-[9px] uppercase tracking-widest px-2.5 py-1 font-bold">
                              Leading Bid
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Consignments List */}
          {activeTab === 'consignments' && (
            <div className="border border-white/5 bg-luxury-charcoal p-8" data-testid={LUNAR.dashboardConsignmentsTable}>
              {consignments.length === 0 ? (
                <div className="text-center py-12 text-luxury-muted font-sans-luxury space-y-4">
                  <AlertCircle size={28} className="mx-auto stroke-[1.2]" />
                  <p className="text-xs">No consignments have been registered to your ledger.</p>
                  <Link to="/consign" className="text-luxury-gold text-[10px] uppercase tracking-widest border-b border-luxury-gold pb-0.5">Submit Your First Specimen</Link>
                </div>
              ) : (
                <div className="space-y-8">
                  {consignments.map((item) => (
                    <div key={item.id} className="border border-white/10 bg-black/40 p-6 md:p-8 flex flex-col md:flex-row gap-6 hover:border-luxury-gold/30 transition-all duration-500">
                      
                      {/* Photo Preview */}
                      <div className="w-full md:w-1/4 aspect-video md:aspect-square overflow-hidden bg-black border border-white/10 relative">
                        <img 
                          src={item.photos?.[0] || 'https://images.unsplash.com/photo-1730110987360-513bb7f20427?auto=format&fit=crop&q=80&w=400'} 
                          alt="Consigned item" 
                          onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                          className="w-full h-full object-cover grayscale brightness-75 hover:grayscale-0 transition-all duration-300" 
                        />
                        {item.photos?.length > 1 && (
                          <span className="absolute bottom-2 right-2 bg-black/70 text-luxury-gold text-[8px] px-1.5 py-0.5 font-mono-luxury">+{item.photos.length - 1}</span>
                        )}
                      </div>

                      {/* Content & Details */}
                      <div className="w-full md:w-3/4 flex flex-col justify-between">
                        <div>
                          <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                            <span className="text-[10px] font-sans-luxury tracking-widest text-luxury-gold uppercase font-bold">Consignment Inquiry</span>
                            <span 
                              className="text-[9px] font-sans-luxury uppercase tracking-widest font-bold px-3 py-1 border bg-blue-500/10 border-blue-500/20 text-blue-400"
                              data-testid={LUNAR.dashboardConsignmentStatus}
                            >
                              Status: {item.status}
                            </span>
                          </div>
                          <p className="text-sm text-luxury-muted leading-relaxed line-clamp-4 font-sans-luxury">{item.item_description}</p>
                          {item.acquisition ? (
                            <p className="text-[11px] text-luxury-muted/80 mt-3 italic leading-relaxed">Provenance note: {item.acquisition}</p>
                          ) : null}
                          <div className="mt-4 border-t border-white/10 pt-4" data-testid="dashboard-consignment-contact">
                            <span className="block text-[9px] uppercase tracking-[0.25em] text-luxury-gold font-semibold mb-2">Contact Information</span>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-[11px] font-sans-luxury">
                              <div>
                                <span className="block text-[8px] uppercase tracking-widest text-luxury-muted mb-0.5">Name</span>
                                <span className="text-white">{item.name || '—'}</span>
                              </div>
                              <div>
                                <span className="block text-[8px] uppercase tracking-widest text-luxury-muted mb-0.5">Email</span>
                                <a href={item.email ? `mailto:${item.email}` : undefined} className="text-luxury-goldLight break-all">{item.email || '—'}</a>
                              </div>
                              <div>
                                <span className="block text-[8px] uppercase tracking-widest text-luxury-muted mb-0.5">Phone</span>
                                <span className="text-white/80">{item.phone || '—'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-white/5 pt-5 mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                          <div className="text-[10px] font-mono-luxury text-luxury-muted">Submitted: {new Date(item.created_at).toLocaleDateString()}</div>
                          <span className="text-[10px] text-luxury-goldLight font-sans-luxury">A specialist will reach out to you personally.</span>
                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Saved Archives Grid */}
          {activeTab === 'saved' && (
            <div className="border border-white/5 bg-luxury-charcoal p-8" data-testid={LUNAR.dashboardSavedGrid}>
              {savedItems.length === 0 ? (
                <div className="text-center py-12 text-luxury-muted font-sans-luxury space-y-4">
                  <AlertCircle size={28} className="mx-auto stroke-[1.2]" />
                  <p className="text-xs">No items saved to your dashboard yet.</p>
                  <Link to="/" className="text-luxury-gold text-[10px] uppercase tracking-widest border-b border-luxury-gold pb-0.5">Browse Live Gallery</Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {savedItems.map((item) => (
                    <div key={item.id} className="bg-black/50 border border-white/5 flex flex-col h-full hover:border-luxury-gold/30 transition-all duration-300">
                      <div className="aspect-[4/3] overflow-hidden bg-black relative">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover grayscale brightness-75 hover:grayscale-0 transition-all duration-300" />
                        <button 
                          onClick={() => removeSavedItem(item.id, item.title)}
                          className="absolute top-3 right-3 p-2 bg-black/60 hover:text-red-400 text-luxury-gold border border-white/10"
                          title="Remove Archive"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="p-5 flex-grow flex flex-col justify-between">
                        <div>
                          <span className="text-[9px] font-sans-luxury tracking-widest text-luxury-gold uppercase font-bold">{item.category}</span>
                          <h4 className="font-serif-luxury text-sm text-white font-bold mt-1 line-clamp-1">{item.title}</h4>
                          <p className="text-xs text-luxury-muted mt-2 line-clamp-2 leading-relaxed">{item.description}</p>
                        </div>
                        <div className="border-t border-white/5 pt-4 mt-4 flex items-center justify-between">
                          <div>
                            <span className="text-[8px] uppercase tracking-widest text-luxury-muted block">Current Bid</span>
                            <span className="text-sm font-mono-luxury font-bold text-luxury-gold">${item.current_bid.toLocaleString()}</span>
                          </div>
                          <Link 
                            to="/"
                            className="text-white hover:text-luxury-gold text-[9px] font-sans-luxury uppercase tracking-widest flex items-center gap-0.5 border-b border-white/20 hover:border-luxury-gold transition-colors duration-300 pb-0.5"
                          >
                            Bid Lot <ArrowUpRight size={10} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin panel is rendered directly above for admins */}

        </div>
        </>
        )}

      </div>
    </div>
  );
}
